// SEO utility functions for managing meta tags and structured data
// This file provides centralized SEO management for all pages

interface SEOConfig {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: object;
}

export const updateSEO = (config: SEOConfig): void => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ranchfireradio.com';

  // Update title
  document.title = config.title;

  // Update or create meta description
  updateMetaTag('name', 'description', config.description);

  // Update or create meta keywords
  updateMetaTag('name', 'keywords', config.keywords);

  // Update canonical URL
  updateLinkTag('canonical', `${siteUrl}${config.canonical}`);

  // Update Open Graph tags
  updateMetaTag('property', 'og:title', config.ogTitle || config.title);
  updateMetaTag('property', 'og:description', config.ogDescription || config.description);
  updateMetaTag('property', 'og:url', `${siteUrl}${config.canonical}`);
  updateMetaTag('property', 'og:type', config.ogType || 'website');
  
  if (config.ogImage) {
    updateMetaTag('property', 'og:image', config.ogImage);
  }

  // Update Twitter Card tags
  updateMetaTag('name', 'twitter:card', config.twitterCard || 'summary_large_image');
  updateMetaTag('name', 'twitter:title', config.twitterTitle || config.title);
  updateMetaTag('name', 'twitter:description', config.twitterDescription || config.description);
  
  if (config.twitterImage) {
    updateMetaTag('name', 'twitter:image', config.twitterImage);
  }

  // Update last-modified date
  const today = new Date().toISOString().split('T')[0];
  updateMetaTag('name', 'last-modified', today);

  // Add or update structured data
  if (config.structuredData) {
    updateStructuredData(config.structuredData);
  }
};

const updateMetaTag = (attribute: string, attributeValue: string, content: string): void => {
  let meta = document.querySelector(`meta[${attribute}="${attributeValue}"]`);
  
  if (meta) {
    meta.setAttribute('content', content);
  } else {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, attributeValue);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
};

const updateLinkTag = (rel: string, href: string): void => {
  let link = document.querySelector(`link[rel="${rel}"]`);
  
  if (link) {
    link.setAttribute('href', href);
  } else {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('href', href);
    document.head.appendChild(link);
  }
};

const updateStructuredData = (data: object): void => {
  const scriptId = 'structured-data-script';
  let script = document.querySelector(`script#${scriptId}`);
  
  if (script) {
    script.remove();
  }
  
  script = document.createElement('script');
  script.id = scriptId;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

// Cleanup function to remove structured data on unmount
export const cleanupStructuredData = (): void => {
  const script = document.querySelector('script#structured-data-script');
  if (script) {
    script.remove();
  }
};
